import { Component, OnInit } from '@angular/core';
import { InAppNotification } from '@models';
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { AccountDbQueryService, AccountDbSaveService } from '@account';
import * as _ from 'lodash';

export interface NotificationsDialogParams {
  proximityUserId: string;
  userName: string;
}

export interface NotificationsDialogResult {
  hasChanges: boolean;
}

@Component({
  selector: 'prox-notifications-dialog',
  templateUrl: './notifications-dialog.component.html',
  styleUrls: ['./notifications-dialog.component.scss'],
  standalone: false,
})
export class NotificationsDialogComponent implements OnInit {
  proximityUserId!: string;
  userName!: string;

  inAppNotifications: InAppNotification[] = [];
  selectedNotification?: InAppNotification;

  isPageReady = false;
  hasChanges = false;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig<NotificationsDialogParams>,
    private dbQueryService: AccountDbQueryService,
    private dbSaveService: AccountDbSaveService,
    private confirmationService: ConfirmationService,
  ) {}

  async ngOnInit() {
    const params = this.config.data;
    if (!params) {
      this.ref.close();
      return;
    }

    this.proximityUserId = params.proximityUserId;
    this.userName = params.userName;

    await this.loadNotifications();
    this.isPageReady = true;

    // Auto-select first notification
    if (this.inAppNotifications.length > 0) {
      this.selectNotification(this.inAppNotifications[0]);
    }
  }

  public static openDialog(dialogService: DialogService, data: NotificationsDialogParams, config?: DynamicDialogConfig) {
    return dialogService.open(NotificationsDialogComponent, {
      closable: false,
      draggable: true,
      resizable: true,
      modal: true,
      width: 'max(50vw, 700px)',
      height: '70vh',
      header: 'Inbox',
      focusOnShow: false,
      ...config,
      data: data,
    });
  }

  private async loadNotifications() {
    this.inAppNotifications = await this.dbQueryService.getInAppNotifications(this.proximityUserId);
  }

  async selectNotification(notification: InAppNotification) {
    this.selectedNotification = notification;

    // Auto-mark as read
    if (!notification.wasRead) {
      notification.wasRead = true;
      await this.saveChanges();
    }
  }

  getEventName(notification: InAppNotification): string {
    if (notification.notificationTemplate?.notificationEvent?.description) {
      return notification.notificationTemplate.notificationEvent.description;
    }
    return this.extractSummary(notification.notificationMessage);
  }

  formatRelativeDate(dateVal: string | Date): string {
    if (!dateVal) return '';
    const date = dateVal instanceof Date ? dateVal : new Date(dateVal);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  private extractSummary(html: string): string {
    if (!html) return 'Notification';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const heading = tempDiv.querySelector('h2, h3');
    if (heading?.textContent) {
      return heading.textContent.trim().substring(0, 50);
    }
    const para = tempDiv.querySelector('p');
    if (para?.textContent) {
      const text = para.textContent.trim();
      return text.length > 50 ? text.substring(0, 47) + '...' : text;
    }
    const plainText = tempDiv.textContent || '';
    return plainText.length > 50 ? plainText.substring(0, 47) + '...' : plainText || 'Notification';
  }

  async onDelete(notification: InAppNotification) {
    notification.entityAspect.setDeleted();
    await this.saveChanges();

    _.pull(this.inAppNotifications, notification);

    if (this.selectedNotification === notification) {
      this.selectedNotification = undefined;
    }
  }

  get unreadCount(): number {
    return this.inAppNotifications.filter((n) => !n.wasRead).length;
  }

  get readCount(): number {
    return this.inAppNotifications.filter((n) => n.wasRead).length;
  }

  async onMarkAllRead() {
    let anyChanged = false;
    this.inAppNotifications.forEach((n) => {
      if (!n.wasRead) {
        n.wasRead = true;
        anyChanged = true;
      }
    });

    if (anyChanged) {
      await this.saveChanges();
    }
  }

  onDeleteAllRead() {
    const readNotifications = this.inAppNotifications.filter((n) => n.wasRead);
    if (readNotifications.length === 0) return;

    const count = readNotifications.length;
    this.confirmationService.confirm({
      key: 'inbox',
      header: 'Delete All Read Messages',
      message: `Are you sure you want to delete ${count} read message${count !== 1 ? 's' : ''}?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        readNotifications.forEach((n) => n.entityAspect.setDeleted());
        await this.saveChanges();

        this.inAppNotifications = this.inAppNotifications.filter((n) => !readNotifications.includes(n));

        if (this.selectedNotification && readNotifications.includes(this.selectedNotification)) {
          this.selectedNotification = undefined;
        }
      },
    });
  }

  private async saveChanges() {
    await this.dbSaveService.saveChanges();
    this.hasChanges = true;
  }

  onClose() {
    const result: NotificationsDialogResult = { hasChanges: this.hasChanges };
    this.ref.close(result);
  }

  onReplyByEmail() {
    if (!this.selectedNotification) return;
    this.openEmailClient(false);
  }

  onForwardByEmail() {
    if (!this.selectedNotification) return;
    this.openEmailClient(true);
  }

  private openEmailClient(isForward: boolean) {
    if (!this.selectedNotification) return;

    const subjectPrefix = isForward ? 'Fwd: ' : 'Re: ';
    const subjectText = 'Proximity Notification';
    const subject = encodeURIComponent(`${subjectPrefix}${subjectText}`);

    const originalContent = this.extractPlainText(this.selectedNotification.notificationMessage);
    const receivedDate = new Date(this.selectedNotification.createdTs).toLocaleString();
    const body = encodeURIComponent(`\n\n\n---------- Original Message ----------\nReceived: ${receivedDate}\n\n${originalContent}`);

    const toEmail = isForward ? '' : this.extractEmailFromContent(this.selectedNotification.notificationMessage);

    window.location.href = `mailto:${toEmail}?subject=${subject}&body=${body}`;
  }

  private extractEmailFromContent(html: string): string {
    if (!html) return '';
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const matches = html.match(emailRegex);
    return matches && matches.length > 0 ? matches[0] : '';
  }

  private extractPlainText(html: string): string {
    if (!html) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    tempDiv.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    tempDiv.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li').forEach((el) => {
      el.prepend(document.createTextNode('\n'));
      el.append(document.createTextNode('\n'));
    });

    let text = tempDiv.textContent || '';
    text = text.replace(/\n{3,}/g, '\n\n').trim();

    return text;
  }
}
